import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Shield, 
  Briefcase, 
  FileText, 
  GraduationCap, 
  Search, 
  MessageSquare,
  ArrowRight,
  CheckCircle,
  Clock,
  Users,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [coachingStats, setCoachingStats] = useState(null);
  const [communityStats, setCommunityStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, coachingRes, communityRes] = await Promise.all([
          axios.get(`${API_URL}/api/dashboard/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/coaching/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: null })),
          axios.get(`${API_URL}/api/community/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: null }))
        ]);
        setStats(statsRes.data);
        setCoachingStats(coachingRes.data);
        setCommunityStats(communityRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const quickActions = [
    { 
      icon: Shield, 
      title: 'Benefits Advisor', 
      description: 'Get guidance on VA benefits',
      path: '/benefits-chat',
      color: 'bg-amber-100 text-amber-600'
    },
    { 
      icon: Briefcase, 
      title: 'Career Coach', 
      description: 'Professional career advice',
      path: '/career-chat',
      color: 'bg-blue-100 text-blue-600'
    },
    { 
      icon: Users, 
      title: 'Coaching', 
      description: 'Book 1-on-1 sessions',
      path: '/coaching',
      color: 'bg-purple-100 text-purple-600'
    },
    { 
      icon: Search, 
      title: 'Job Search', 
      description: 'Find veteran-friendly jobs',
      path: '/jobs',
      color: 'bg-emerald-100 text-emerald-600'
    }
  ];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="dashboard-page">
        {/* Welcome Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="font-heading text-3xl font-bold text-navy-900 dark:text-white mb-2">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-navy-600 dark:text-navy-300">
            Continue your journey to civilian career success
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {quickActions.map((action, idx) => (
            <Link key={action.path} to={action.path}>
              <Card 
                className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-navy-100 dark:border-navy-700 dark:bg-navy-800 cursor-pointer group"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <CardContent className="p-5">
                  <div className={`w-12 h-12 rounded-xl ${action.color} dark:opacity-90 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <action.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading font-semibold text-navy-900 dark:text-white mb-1">{action.title}</h3>
                  <p className="text-sm text-navy-500 dark:text-navy-400">{action.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-navy-500 dark:text-navy-400 flex items-center gap-2">
                <Search className="w-4 h-4" />
                Job Search
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-4">
                <span className="text-3xl font-bold text-navy-900 dark:text-white">{stats?.saved_jobs || 0}</span>
                <span className="text-navy-500 dark:text-navy-400">saved</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-emerald-600 dark:text-emerald-400">{stats?.applied_jobs || 0} applications sent</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-navy-500 dark:text-navy-400 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-4">
                <span className="text-3xl font-bold text-navy-900 dark:text-white">{stats?.documents_created || 0}</span>
                <span className="text-navy-500 dark:text-navy-400">created</span>
              </div>
              <Link to="/documents" className="mt-2 inline-flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700">
                Create new document <ArrowRight className="w-4 h-4" />
              </Link>
            </CardContent>
          </Card>

          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-navy-500 dark:text-navy-400 flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                Learning Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-4 mb-2">
                <span className="text-3xl font-bold text-navy-900 dark:text-white">{stats?.courses_completed || 0}</span>
                <span className="text-navy-500 dark:text-navy-400">of {stats?.total_courses || 0} courses</span>
              </div>
              <Progress 
                value={stats?.total_courses ? (stats.courses_completed / stats.total_courses) * 100 : 0} 
                className="h-2"
              />
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Coaching Session */}
        {coachingStats?.next_session && (
          <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-white dark:from-amber-900/20 dark:to-navy-800 mb-8">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Upcoming Coaching Session</p>
                    <h3 className="font-heading text-lg font-semibold text-navy-900 dark:text-white">
                      {coachingStats.next_session.session_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </h3>
                    <div className="flex items-center gap-3 text-sm text-navy-500 dark:text-navy-400 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(coachingStats.next_session.date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {coachingStats.next_session.start_time}
                      </span>
                    </div>
                  </div>
                </div>
                <Link to="/coaching">
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    View Details
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Chats */}
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2 dark:text-white">
                <MessageSquare className="w-5 h-5 text-amber-600" />
                Recent Conversations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.recent_chats?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_chats.slice(0, 4).map((chat) => (
                    <Link 
                      key={chat.session_id}
                      to={`/${chat.chat_type}-chat`}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-navy-50 dark:hover:bg-navy-700 transition-colors"
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        chat.chat_type === 'benefits' ? 'bg-amber-100' : 'bg-blue-100'
                      }`}>
                        {chat.chat_type === 'benefits' ? (
                          <Shield className="w-5 h-5 text-amber-600" />
                        ) : (
                          <Briefcase className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-navy-900 dark:text-white capitalize">
                          {chat.chat_type} Chat
                        </p>
                        <p className="text-sm text-navy-500 dark:text-navy-400 truncate">
                          {chat.messages?.length || 0} messages
                        </p>
                      </div>
                      <Clock className="w-4 h-4 text-navy-400" />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-navy-200 dark:text-navy-600 mx-auto mb-3" />
                  <p className="text-navy-500 dark:text-navy-400">No conversations yet</p>
                  <Link to="/benefits-chat">
                    <Button className="mt-4 bg-amber-600 hover:bg-amber-700">
                      Start a Chat
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Profile Completion */}
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2 dark:text-white">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
                Continue Learning
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-navy-900 to-navy-800 rounded-xl text-white">
                  <h4 className="font-semibold mb-1">Military-to-Civilian Resume</h4>
                  <p className="text-sm text-navy-200 mb-3">Learn to translate your military experience</p>
                  <div className="flex items-center justify-between">
                    <Progress value={0} className="w-32 h-2 bg-navy-700" />
                    <Link to="/learning">
                      <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white">
                        Start
                      </Button>
                    </Link>
                  </div>
                </div>

                <div className="p-4 border border-navy-200 dark:border-navy-600 rounded-xl">
                  <h4 className="font-semibold text-navy-900 dark:text-white mb-1">Interview Skills</h4>
                  <p className="text-sm text-navy-500 dark:text-navy-400 mb-3">Master civilian job interviews</p>
                  <Link to="/learning" className="text-amber-600 text-sm font-medium hover:text-amber-700">
                    View Course →
                  </Link>
                </div>

                <Link to="/learning">
                  <Button variant="outline" className="w-full border-navy-200 dark:border-navy-600 dark:text-white dark:hover:bg-navy-700">
                    View All Courses
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Community Activity */}
        {communityStats && (
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2 dark:text-white">
                  <MessageSquare className="w-5 h-5 text-purple-600" />
                  Community Activity
                </CardTitle>
                <Link to="/community">
                  <Button variant="outline" size="sm" className="dark:border-navy-600 dark:text-white">
                    Enter Community
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {communityStats.posts_this_week || 0}
                  </p>
                  <p className="text-sm text-navy-500 dark:text-navy-400">Posts This Week</p>
                </div>
                <div className="p-3 bg-navy-50 dark:bg-navy-700/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-navy-900 dark:text-white">
                    {communityStats.total_posts || 0}
                  </p>
                  <p className="text-sm text-navy-500 dark:text-navy-400">Total Discussions</p>
                </div>
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                    {communityStats.user_posts || 0}
                  </p>
                  <p className="text-sm text-navy-500 dark:text-navy-400">Your Posts</p>
                </div>
              </div>
              {communityStats.latest_activity?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-navy-600 dark:text-navy-300 mb-2">Latest Activity</p>
                  {communityStats.latest_activity.map((post) => (
                    <Link 
                      key={post.post_id}
                      to="/community"
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-navy-50 dark:hover:bg-navy-700 transition-colors"
                    >
                      <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-navy-900 dark:text-white truncate">{post.title}</p>
                        <p className="text-xs text-navy-500 dark:text-navy-400">
                          by {post.user_name} • {post.comments_count || 0} comments
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
